import { ChartConfig, ChartContainer } from "design-system/molecules/Chart"
import { cn } from "design-system/utils"
import { formatNumber } from "lopermut"
import { getSentimentColor, getSentimentLabel } from "modules/shared/lib/sentiment"
import { useTranslation } from "react-i18next"
import { Cell, Label, Pie, PieChart } from "recharts"

const chartConfig = {} satisfies ChartConfig

type Props = {
  sentiment: number | null | undefined
  mini?: boolean
}

export function SentimentScore({ sentiment, mini }: Props) {
  const { t } = useTranslation()

  const noData = !sentiment
  const chartSentiment = sentiment ?? 0

  const chartData = [
    { value: chartSentiment }, // Active segment
    { value: 4 - chartSentiment }, // Background/remaining segment
  ]

  return (
    <ChartContainer
      config={chartConfig}
      className={cn(
        "aspect-square",
        mini ? "h-[14px]" : "h-[100px]",
        noData && "pointer-events-none grayscale opacity-60 dark:opacity-5",
      )}
    >
      <PieChart>
        <Pie
          data={chartData}
          dataKey="value"
          startAngle={90}
          endAngle={-270}
          innerRadius={mini ? 5 : 40}
          outerRadius={mini ? 7 : 50}
          cornerRadius={0}
          animationDuration={0}
        >
          <Cell fill={`var(--${getSentimentColor(chartSentiment)})`} /> {/* Active segment */}
          <Cell fill="var(--muted)" /> {/* Background segment */}
          {!mini && (
            <Label
              content={({ viewBox }) => {
                if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                  return (
                    <text
                      x={viewBox.cx}
                      y={viewBox.cy}
                      textAnchor="middle"
                      dominantBaseline="middle"
                    >
                      <tspan
                        x={viewBox.cx}
                        y={(viewBox.cy ?? 0) - 6}
                        className="fill-muted-foreground"
                      >
                        {noData ? (
                          "- / 4"
                        ) : (
                          <>
                            {formatNumber({ amount: chartSentiment, fix: 1 })} {"/ 4"}
                          </>
                        )}
                      </tspan>
                      <tspan
                        x={viewBox.cx}
                        y={(viewBox.cy ?? 0) + 10}
                        className="text-[10px] fill-muted-foreground"
                      >
                        {noData ? t("shared.noInfo") : getSentimentLabel(chartSentiment, t)}
                      </tspan>
                    </text>
                  )
                }
              }}
            />
          )}
        </Pie>
      </PieChart>
    </ChartContainer>
  )
}
